import { IRouteLimit } from 'services/routeService'

type CauseFallback = {
  boxesCount?: number | null
  capacityVehicle?: number | null
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR') : 'horário não informado'

export const routeOverflowSourceLabel = (source?: string | null) =>
  ({
    separacao: 'separação',
    conferencia: 'conferência',
    reconferencia: 'reconferência',
    reposicao: 'reposição',
    leitura_automatica_esteira: 'leitura automática da esteira',
    correcao_manual_esteira: 'correção manual da esteira',
    ajuste_carregamento: 'ajuste do carregamento',
    reducao_rota_estourada: 'redução de rota estourada',
    finalizacao_rotas: 'finalização de rotas',
  }[source || ''] ||
  source ||
  'origem não identificada')

const formatCountChange = (change: {
  order_id: string
  previous_box_count?: number | null
  current_box_count: number
}) => {
  if (change.previous_box_count === null || change.previous_box_count === undefined) {
    return `${change.order_id} (${change.current_box_count} caixas registradas)`
  }
  if (Number(change.previous_box_count) === Number(change.current_box_count)) {
    return `${change.order_id} (${change.current_box_count} caixas confirmadas)`
  }
  return `${change.order_id} (${change.previous_box_count} → ${change.current_box_count})`
}

export const routeOverflowCauseSummary = (causeType: string) =>
  ({
    order_box_count_changed: 'Quantidade de caixas de um pedido alterada',
    multiple_order_box_counts: 'Quantidades de pedidos atualizadas juntas',
    order_added_to_route: 'Pedido incluído na rota',
    order_moved_to_route: 'Pedido transferido para a rota',
    order_removed_from_route: 'Pedido retirado da rota',
    vehicle_capacity_changed: 'Capacidade máxima do veículo alterada',
    vehicle_changed: 'Veículo da rota alterado',
    initial_route_composition: 'Rota já estava acima da capacidade na verificação',
    unknown: 'Motivo do excesso não identificado',
  }[causeType] || 'Motivo do excesso não identificado')

export const describeRouteOverflowCause = (
  incident?: IRouteLimit | null,
  fallback: CauseFallback = {},
) => {
  if (!incident) return 'Não foi possível identificar o evento que abriu o incidente.'

  const cause = incident.cause_details || {}
  const auditSuffix = ` Em ${formatDate(incident.detected_at)}${
    incident.triggered_by ? `, por ${incident.triggered_by}` : ''
  }.`

  switch (incident.cause_type) {
    case 'vehicle_capacity_changed':
      return `A capacidade máxima do veículo foi alterada de ${
        incident.previous_capacity ?? '?'
      } para ${incident.current_capacity ?? '?'} caixas.${auditSuffix}`
    case 'vehicle_changed':
      return `O veículo foi trocado de ${cause.previous_license_plate || 'não informado'} para ${
        cause.current_license_plate || 'não informado'
      }. A capacidade máxima passou de ${incident.previous_capacity ?? '?'} para ${
        incident.current_capacity ?? '?'
      } caixas.${auditSuffix}`
    case 'order_added_to_route':
      return `O pedido ${incident.trigger_order_id} foi incluído nesta rota com ${
        cause.order_box_count ?? '?'
      } caixas.${auditSuffix}`
    case 'order_moved_to_route':
      return `O pedido ${incident.trigger_order_id} foi transferido da rota ${
        cause.from_route ?? 'anterior'
      } para esta rota, com ${cause.order_box_count ?? '?'} caixas.${auditSuffix}`
    case 'order_removed_from_route':
      return `O pedido ${incident.trigger_order_id} foi retirado desta rota.${auditSuffix}`
    case 'order_box_count_changed': {
      const event = incident.trigger_event
      const previousCount = cause.previous_box_count
      const currentCount = cause.current_box_count
      const origin = event?.source_label || routeOverflowSourceLabel(incident.trigger_source)
      const operator = event?.created_by ? ` por ${event.created_by}` : ''

      if (previousCount === undefined || previousCount === null) {
        return `Foram registradas ${currentCount ?? '?'} caixas para o pedido ${
          incident.trigger_order_id
        } durante ${origin}${operator}. O sistema identificou que a rota estava acima da capacidade, em ${formatDate(
          event?.created_at,
        )}.`
      }

      if (
        currentCount !== undefined &&
        currentCount !== null &&
        Number(previousCount) === Number(currentCount)
      ) {
        return `Ao registrar ${currentCount} caixas para o pedido ${
          incident.trigger_order_id
        } durante ${origin}${operator}, o sistema identificou que a rota estava acima da capacidade, em ${formatDate(
          event?.created_at,
        )}.`
      }

      return `A quantidade do pedido ${incident.trigger_order_id} mudou de ${
        previousCount ?? '?'
      } para ${currentCount ?? '?'} caixas durante ${origin}${operator}, em ${formatDate(
        event?.created_at,
      )}.`
    }
    case 'multiple_order_box_counts': {
      const changes = cause.changes || []
      const changedOrders = changes.slice(0, 3).map(formatCountChange).join(', ')
      const remainingOrders = changes.length - 3
      return `${
        changes.length || 'Várias'
      } atualizações de caixas foram aplicadas juntas: ${changedOrders}${
        remainingOrders > 0 ? ` e mais ${remainingOrders} pedido(s)` : ''
      }.${auditSuffix}`
    }
    case 'initial_route_composition':
      return `Ao verificar a rota, ela já tinha ${
        incident.detected_total ?? fallback.boxesCount ?? 'quantidade não informada'
      } caixas para uma capacidade de ${
        incident.detected_capacity ?? fallback.capacityVehicle ?? 'não informada'
      } caixas.${auditSuffix}`
    case 'unknown':
      return `A rota está acima da capacidade, mas não foi possível identificar qual alteração causou o excesso.${auditSuffix}`
    default:
      return 'A rota está acima da capacidade, mas o motivo ainda não foi identificado.'
  }
}

const resolutionLabels: Record<string, string> = {
  order_reduction: 'Pedido reduzido',
  order_box_count_changed: 'Quantidade de caixas atualizada',
  order_removed: 'Pedido removido da rota',
  order_moved: 'Pedido transferido',
  vehicle_capacity_changed: 'Capacidade alterada',
  vehicle_changed: 'Veículo alterado',
  automatic_reconciliation: 'Regularização automática',
  unknown: 'Forma não identificada',
}

export const formatRouteOverflowDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

export const describeRouteOverflowResolution = (incident: IRouteLimit) => {
  if (!incident.detected_at) {
    return { title: 'Sem detalhes disponíveis', detail: null, metadata: null }
  }
  if (incident.status === 'pending') {
    return { title: 'Aguardando regularização', detail: null, metadata: null }
  }
  if (incident.status === 'allowed_over_capacity') {
    return {
      title: 'Autorizada acima da capacidade',
      detail: incident.allowed_over_capacity_reason || 'Justificativa não informada',
      metadata:
        [
          formatRouteOverflowDate(incident.allowed_over_capacity_at),
          incident.allowed_over_capacity_by,
        ]
          .filter(Boolean)
          .join(' · ') || null,
    }
  }

  return {
    title:
      incident.resolution_label ||
      resolutionLabels[incident.resolution_type || ''] ||
      'Forma não identificada',
    detail: null,
    metadata:
      [formatRouteOverflowDate(incident.resolved_at), incident.resolved_by]
        .filter(Boolean)
        .join(' · ') || null,
  }
}
